import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatCard } from './StatCard';
import { InviteStaffDialog } from './InviteStaffDialog';
import {
  Users, Home, FolderKanban, Shield, FileCheck, AlertTriangle,
  UserCheck, Building2, BarChart3, MapPin, MessageSquare, Copy,
  UserPlus, Megaphone, Star, ScrollText, Settings2, UsersRound,
  TrendingUp, TrendingDown, Activity, AlertCircle, Clock, ArrowRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const startOfToday = () => { const d = new Date(); d.setHours(0,0,0,0); return d.toISOString(); };
const startOfYesterday = () => { const d = new Date(); d.setDate(d.getDate()-1); d.setHours(0,0,0,0); return d.toISOString(); };
const daysAgoIso = (n: number) => { const d = new Date(); d.setDate(d.getDate()-n); return d.toISOString(); };
const ageDays = (iso?: string | null) => iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)) : null;

export function AdminDashboard() {
  const navigate = useNavigate();
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-landing-v2'],
    queryFn: async () => {
      const today = startOfToday();
      const yesterday = startOfYesterday();
      const last30 = daysAgoIso(30);
      const stale7 = daysAgoIso(7);

      const headCount = (q: any) => q.select('*', { count: 'exact', head: true });

      const [
        // Pending counts
        propsPending, plotsPending, projectsPending, reviewsPending, reportsOpen,
        devApps, agencyApps,
        // Today
        signupsToday, signupsYesterday,
        propsToday, plotsToday, projectsToday,
        propsYday, plotsYday, projectsYday,
        leadsToday, leadsYesterday,
        // Revenue 30d
        revenue30,
        // Totals
        totalProps, totalProjects, totalUsers,
        verifiedAgencies, totalAgencySeats,
        // Health
        teams, teamsNoLeader, superadminCount,
        propsNoImages, staleListings,
        lastBroadcast,
        // Oldest pending (SLA)
        propOldest, plotOldest, projectOldest, reviewOldest, reportOldest,
        devAppOldest, agencyAppOldest,
      ] = await Promise.all([
        headCount(supabase.from('properties')).eq('approval_status','pending'),
        headCount(supabase.from('plots')).eq('approval_status','pending'),
        headCount(supabase.from('projects')).eq('approval_status','pending'),
        headCount(supabase.from('reviews')).eq('status','pending'),
        headCount(supabase.from('reports')).eq('status','open'),
        headCount(supabase.from('developer_applications')).eq('status','pending'),
        headCount(supabase.from('agency_applications')).eq('status','pending'),

        headCount(supabase.from('profiles')).gte('created_at', today),
        headCount(supabase.from('profiles')).gte('created_at', yesterday).lt('created_at', today),

        headCount(supabase.from('properties')).gte('created_at', today),
        headCount(supabase.from('plots')).gte('created_at', today),
        headCount(supabase.from('projects')).gte('created_at', today),
        headCount(supabase.from('properties')).gte('created_at', yesterday).lt('created_at', today),
        headCount(supabase.from('plots')).gte('created_at', yesterday).lt('created_at', today),
        headCount(supabase.from('projects')).gte('created_at', yesterday).lt('created_at', today),

        headCount(supabase.from('leads')).gte('created_at', today),
        headCount(supabase.from('leads')).gte('created_at', yesterday).lt('created_at', today),

        supabase.from('invoices').select('amount_etb').eq('status','paid').gte('issued_at', last30),

        headCount(supabase.from('properties')),
        headCount(supabase.from('projects')),
        headCount(supabase.from('profiles')),
        headCount(supabase.from('agencies')).eq('is_verified', true),
        headCount(supabase.from('agent_profiles')).not('agency_id', 'is', null),

        headCount(supabase.from('internal_teams')),
        headCount(supabase.from('internal_teams')).is('team_leader_id', null),
        headCount(supabase.from('user_roles')).eq('role','superadmin'),

        // Properties without images: count properties whose id NOT in property_images is expensive — approximate via property_images distinct
        supabase.from('properties').select('id', { count: 'exact', head: false }).eq('approval_status','approved').limit(1),
        headCount(supabase.from('properties')).eq('approval_status','pending').lt('created_at', stale7),

        supabase.from('broadcasts').select('sent_at,title').not('sent_at','is',null).order('sent_at',{ ascending: false }).limit(1).maybeSingle(),

        supabase.from('properties').select('created_at').eq('approval_status','pending').order('created_at',{ ascending: true }).limit(1).maybeSingle(),
        supabase.from('plots').select('created_at').eq('approval_status','pending').order('created_at',{ ascending: true }).limit(1).maybeSingle(),
        supabase.from('projects').select('created_at').eq('approval_status','pending').order('created_at',{ ascending: true }).limit(1).maybeSingle(),
        supabase.from('reviews').select('created_at').eq('status','pending').order('created_at',{ ascending: true }).limit(1).maybeSingle(),
        supabase.from('reports').select('created_at').eq('status','open').order('created_at',{ ascending: true }).limit(1).maybeSingle(),
        supabase.from('developer_applications').select('created_at').eq('status','pending').order('created_at',{ ascending: true }).limit(1).maybeSingle(),
        supabase.from('agency_applications').select('created_at').eq('status','pending').order('created_at',{ ascending: true }).limit(1).maybeSingle(),
      ]);

      const revenue = (revenue30.data || []).reduce((s: number, r: any) => s + Number(r.amount_etb || 0), 0);

      return {
        pending: {
          properties: propsPending.count || 0,
          plots: plotsPending.count || 0,
          projects: projectsPending.count || 0,
          reviews: reviewsPending.count || 0,
          reports: reportsOpen.count || 0,
          devApps: devApps.count || 0,
          agencyApps: agencyApps.count || 0,
        },
        oldest: {
          properties: ageDays(propOldest.data?.created_at),
          plots: ageDays(plotOldest.data?.created_at),
          projects: ageDays(projectOldest.data?.created_at),
          reviews: ageDays(reviewOldest.data?.created_at),
          reports: ageDays(reportOldest.data?.created_at),
          devApps: ageDays(devAppOldest.data?.created_at),
          agencyApps: ageDays(agencyAppOldest.data?.created_at),
        },
        pulse: {
          signupsToday: signupsToday.count || 0,
          signupsYesterday: signupsYesterday.count || 0,
          listingsToday: (propsToday.count||0)+(plotsToday.count||0)+(projectsToday.count||0),
          listingsYesterday: (propsYday.count||0)+(plotsYday.count||0)+(projectsYday.count||0),
          leadsToday: leadsToday.count || 0,
          leadsYesterday: leadsYesterday.count || 0,
          revenue30: revenue,
        },
        totals: {
          properties: totalProps.count || 0,
          projects: totalProjects.count || 0,
          users: totalUsers.count || 0,
          verifiedAgencies: verifiedAgencies.count || 0,
          agencySeats: totalAgencySeats.count || 0,
        },
        health: {
          teams: teams.count || 0,
          teamsNoLeader: teamsNoLeader.count || 0,
          superadmins: superadminCount.count || 0,
          staleListings: staleListings.count || 0,
          lastBroadcast: lastBroadcast.data,
        },
      };
    },
  });

  const actionChips = useMemo(() => {
    if (!data) return [];
    const p = data.pending;
    return [
      { label: 'Properties', count: p.properties, path: '/dashboard/property-approvals' },
      { label: 'Plots', count: p.plots, path: '/dashboard/plot-approvals' },
      { label: 'Projects', count: p.projects, path: '/dashboard/project-approvals' },
      { label: 'Reviews', count: p.reviews, path: '/dashboard/reviews-moderation' },
      { label: 'Reports', count: p.reports, path: '/dashboard/reports' },
      { label: 'Dev Apps', count: p.devApps, path: '/dashboard/developer-applications' },
      { label: 'Agency Apps', count: p.agencyApps, path: '/dashboard/agency-applications' },
    ].filter(c => c.count > 0);
  }, [data]);

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 rounded-xl" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  const delta = (today: number, yesterday: number) => {
    if (yesterday === 0) return today > 0 ? 100 : 0;
    return Math.round(((today - yesterday) / yesterday) * 100);
  };
  const signupDelta = delta(data.pulse.signupsToday, data.pulse.signupsYesterday);
  const listingDelta = delta(data.pulse.listingsToday, data.pulse.listingsYesterday);
  const leadDelta = delta(data.pulse.leadsToday, data.pulse.leadsYesterday);

  const moderationTiles = [
    { label: 'Property Approvals', icon: Home, count: data.pending.properties, age: data.oldest.properties, path: '/dashboard/property-approvals', color: 'text-yellow-600 bg-yellow-100' },
    { label: 'Plot Approvals', icon: MapPin, count: data.pending.plots, age: data.oldest.plots, path: '/dashboard/plot-approvals', color: 'text-orange-600 bg-orange-100' },
    { label: 'Project Approvals', icon: FolderKanban, count: data.pending.projects, age: data.oldest.projects, path: '/dashboard/project-approvals', color: 'text-blue-600 bg-blue-100' },
    { label: 'Reviews Moderation', icon: MessageSquare, count: data.pending.reviews, age: data.oldest.reviews, path: '/dashboard/reviews-moderation', color: 'text-purple-600 bg-purple-100' },
    { label: 'Reports & Abuse', icon: AlertTriangle, count: data.pending.reports, age: data.oldest.reports, path: '/dashboard/reports', color: 'text-rose-600 bg-rose-100' },
    { label: 'Duplicates Detected', icon: Copy, count: 0, age: null, path: '/dashboard/duplicates', color: 'text-slate-600 bg-slate-100' },
  ];

  const onboardingTiles = [
    { label: 'Agency Applications', icon: Shield, count: data.pending.agencyApps, age: data.oldest.agencyApps, path: '/dashboard/agency-applications', color: 'text-cyan-600 bg-cyan-100' },
    { label: 'Developer Applications', icon: Building2, count: data.pending.devApps, age: data.oldest.devApps, path: '/dashboard/developer-applications', color: 'text-emerald-600 bg-emerald-100' },
  ];

  const quickActions = [
    { label: 'Invite Staff Member', icon: UserPlus, onClick: () => setInviteOpen(true), color: 'bg-primary/10 text-primary' },
    { label: 'Send Broadcast', icon: Megaphone, onClick: () => navigate('/dashboard/broadcasts'), color: 'bg-orange-500/10 text-orange-600' },
    { label: 'Featured Listings', icon: Star, onClick: () => navigate('/dashboard/featured-listings'), color: 'bg-yellow-500/10 text-yellow-600' },
    { label: 'Audit Log', icon: ScrollText, onClick: () => navigate('/dashboard/audit-log'), color: 'bg-slate-500/10 text-slate-600' },
    { label: 'System Settings', icon: Settings2, onClick: () => navigate('/dashboard/system-settings'), color: 'bg-muted text-muted-foreground' },
    { label: 'Internal Teams', icon: UsersRound, onClick: () => navigate('/dashboard/internal-teams'), color: 'bg-emerald-500/10 text-emerald-600' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold">Super Admin Dashboard</h2>
        <p className="text-sm text-muted-foreground mt-1">Real-time platform health, moderation queues and admin tools.</p>
      </div>

      {/* Section 1 — Action Required */}
      {actionChips.length > 0 && (
        <Card className="rounded-2xl border-0 shadow-sm bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex items-center gap-2 shrink-0">
                <div className="h-9 w-9 rounded-xl bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                </div>
                <span className="font-semibold text-sm">Action Required</span>
              </div>
              <div className="flex flex-wrap gap-2 flex-1">
                {actionChips.map(chip => (
                  <button
                    key={chip.label}
                    onClick={() => navigate(chip.path)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
                  >
                    <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-bold bg-amber-500 text-white">{chip.count}</Badge>
                    {chip.label}
                    <ArrowRight className="h-3 w-3" />
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 2 — Today's Pulse */}
      <div>
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Today's Pulse</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <PulseCard label="New Signups Today" value={data.pulse.signupsToday} delta={signupDelta} icon={Users} variant="sale" />
          <PulseCard label="New Listings Today" value={data.pulse.listingsToday} delta={listingDelta} icon={Home} variant="rent" />
          <PulseCard label="Leads Today" value={data.pulse.leadsToday} delta={leadDelta} icon={Activity} variant="customer" />
          <StatCard
            value={`${(data.pulse.revenue30/1000).toFixed(1)}K`}
            label="Revenue 30d (ETB)"
            variant="city"
            progress={Math.min(100, Math.round((data.pulse.revenue30/100000)*100))}
          />
        </div>
      </div>

      {/* Section 3 — Moderation Queues */}
      <div>
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Moderation Queues</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {moderationTiles.map(t => <QueueTile key={t.label} {...t} onClick={() => navigate(t.path)} />)}
        </div>
      </div>

      {/* Section 4 — Onboarding Queue */}
      <div>
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Onboarding Queue</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {onboardingTiles.map(t => <QueueTile key={t.label} {...t} onClick={() => navigate(t.path)} />)}
        </div>
      </div>

      {/* Section 5 — Quick Actions */}
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-5">
          <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" /> Quick Actions
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {quickActions.map(a => (
              <button
                key={a.label}
                onClick={a.onClick}
                className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors text-left"
              >
                <div className={`h-9 w-9 rounded-lg ${a.color} flex items-center justify-center`}>
                  <a.icon className="h-4 w-4" />
                </div>
                <span className="text-xs font-medium">{a.label}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Section 6 — System & Team Health */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
              <UsersRound className="h-4 w-4 text-primary" /> Internal Teams
            </h3>
            <div className="space-y-3">
              <HealthRow label="Active internal teams" value={data.health.teams} onClick={() => navigate('/dashboard/internal-teams')} />
              <HealthRow label="Active verified agencies" value={data.totals.verifiedAgencies} onClick={() => navigate('/agencies')} />
              <HealthRow label="Total agency seats (members)" value={data.totals.agencySeats} onClick={() => navigate('/dashboard/users?role=agent')} />
              <HealthRow
                label="Teams without a Team Leader"
                value={data.health.teamsNoLeader}
                warn={data.health.teamsNoLeader > 0}
                onClick={() => navigate('/dashboard/internal-teams')}
              />
              <HealthRow
                label="Active superadmins"
                value={data.health.superadmins}
                warn={data.health.superadmins <= 1}
                onClick={() => navigate('/dashboard/users')}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" /> Platform Health
            </h3>
            <div className="space-y-3">
              <HealthRow
                label="Listings stuck pending > 7 days"
                value={data.health.staleListings}
                warn={data.health.staleListings > 0}
                onClick={() => navigate('/dashboard/property-approvals')}
              />
              <HealthRow label="Total properties on platform" value={data.totals.properties} onClick={() => navigate('/dashboard/listings')} />
              <HealthRow label="Total projects on platform" value={data.totals.projects} onClick={() => navigate('/dashboard/my-projects')} />
              <div
                onClick={() => navigate('/dashboard/broadcasts')}
                className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Last broadcast sent</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {data.health.lastBroadcast?.sent_at
                    ? formatDistanceToNow(new Date(data.health.lastBroadcast.sent_at), { addSuffix: true })
                    : 'Never'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <InviteStaffDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}

function PulseCard({ label, value, delta, icon: Icon, variant }: { label: string; value: number; delta: number; icon: any; variant: 'sale'|'rent'|'customer'|'city' }) {
  const progress = Math.max(0, Math.min(100, 50 + delta));
  return (
    <div className="relative">
      <StatCard value={value} label={label} variant={variant} progress={progress} />
      <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 text-white text-[10px] font-semibold">
        {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        {delta > 0 ? '+' : ''}{delta}%
      </div>
    </div>
  );
}

function QueueTile({ label, icon: Icon, count, age, color, onClick }: { label: string; icon: any; count: number; age: number | null; color: string; onClick: () => void }) {
  const slaBreach = age !== null && age > 3;
  return (
    <Card
      className={`rounded-2xl border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${slaBreach ? 'ring-2 ring-rose-300 dark:ring-rose-900' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`h-12 w-12 rounded-xl ${color} flex items-center justify-center shrink-0`}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <div className="text-2xl font-bold">{count}</div>
              {count > 0 && age !== null && (
                <div className={`text-[10px] font-medium ${slaBreach ? 'text-rose-600' : 'text-muted-foreground'}`}>
                  oldest: {age}d
                </div>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function HealthRow({ label, value, warn, onClick }: { label: string; value: number; warn?: boolean; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
    >
      <div className="flex items-center gap-2">
        {warn ? <AlertTriangle className="h-4 w-4 text-rose-500" /> : <CheckDot />}
        <span className="text-sm">{label}</span>
      </div>
      <span className={`text-sm font-bold ${warn ? 'text-rose-600' : ''}`}>{value.toLocaleString()}</span>
    </div>
  );
}

function CheckDot() {
  return <span className="h-2 w-2 rounded-full bg-emerald-500 inline-block" />;
}
